// src/pages/OnboardingPage.js
import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router-dom';
import { Timestamp } from 'firebase/firestore';
import { ArrowLeft, ArrowRight, Loader, Check } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import userService from '../services/userService';
import { trackEvent } from '../services/analyticsService';
import { validateDateOfBirth } from '../utils';
import { FAMILLES } from '../constants';
import { Btn } from '../components/ui';
import {
  ProgressBar,
  StepIdentite,
  StepLocalisation,
  StepMode,
  StepPhoto,
} from '../components/onboarding';

const OnboardingPage = ({ user, onUpdate, toast }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [data, setData] = useState({
    name: '',
    dateOfBirth: '',
    gender: '',
    clan: '',
    city: '',
    cityPostalCode: '',
    cityLat: null,
    cityLng: null,
    mode: '',
    profilePictureFile: null,
    profilePicture: '',
    _photoError: null,
  });

  const handleChange = (partial) => {
    setData((prev) => ({ ...prev, ...partial }));
    // Effacer les erreurs des champs modifiés
    const clearedErrors = { ...errors };
    Object.keys(partial).forEach((k) => delete clearedErrors[k]);
    setErrors(clearedErrors);
  };

  // 4 étapes fixes : Identité → Localisation → Mode → Photo
  const steps = [
    { key: 'identite', label: t('onboarding.steps.identite') },
    { key: 'localisation', label: t('onboarding.steps.localisation') },
    { key: 'mode', label: t('onboarding.steps.mode') },
    { key: 'photo', label: t('onboarding.steps.photo') },
  ];
  const currentStepDef = steps[step - 1];

  const validateStep = () => {
    const e = {};

    switch (currentStepDef.key) {
      case 'identite': {
        if (!data.name.trim()) e.name = t('onboarding.validation.nameRequired');
        else if (data.name.trim().length > 50) e.name = t('onboarding.validation.nameMax');

        const dobResult = validateDateOfBirth(data.dateOfBirth);
        if (!dobResult.valid) e.dateOfBirth = dobResult.error;

        if (!data.gender) e.gender = t('onboarding.validation.genderRequired');
        if (data.clan && !FAMILLES.includes(data.clan))
          e.clan = t('onboarding.validation.clanInvalid');
        break;
      }
      case 'localisation':
        if (!data.city) e.city = t('onboarding.validation.cityRequired');
        break;
      case 'mode':
        if (!data.mode) e.mode = t('onboarding.validation.modeRequired');
        break;
      case 'photo':
        if (!data.profilePictureFile && !data.profilePicture)
          e.profilePictureFile = t('onboarding.validation.photoRequired');
        if (data._photoError) e.profilePictureFile = data._photoError;
        break;
      default:
        break;
    }

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    if (!validateStep()) return;
    if (step < steps.length) {
      setStep(step + 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = () => {
    if (step > 1) {
      setStep(step - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleSubmit = async () => {
    if (!validateStep()) return;
    setLoading(true);

    const payload = {
      name: data.name.trim(),
      dateOfBirth: Timestamp.fromDate(new Date(data.dateOfBirth)),
      gender: data.gender,
      clan: data.clan,
      city: data.city,
      cityPostalCode: data.cityPostalCode,
      cityLat: data.cityLat,
      cityLng: data.cityLng,
      mode: data.mode,
      profilePictureFile: data.profilePictureFile || null,
      profilePicture: data.profilePicture || '',
    };

    const result = await userService.completeOnboarding(user.uid, payload);
    setLoading(false);

    if (result.success) {
      trackEvent('sign_up', { method: 'email' });
      onUpdate(result.user);
      toast(t('onboarding.toastWelcome'), 'success');
      navigate('/');
    } else {
      setErrors({ _global: result.error || t('onboarding.toastError') });
    }
  };

  const isLastStep = step === steps.length;

  const renderStep = () => {
    switch (currentStepDef.key) {
      case 'identite':
        return <StepIdentite data={data} onChange={handleChange} errors={errors} />;
      case 'localisation':
        return <StepLocalisation data={data} onChange={handleChange} errors={errors} />;
      case 'mode':
        return <StepMode data={data} onChange={handleChange} errors={errors} />;
      case 'photo':
        return <StepPhoto data={data} onChange={handleChange} errors={errors} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-white to-rose-50/30 dark:from-gray-950 dark:via-gray-900 dark:to-gray-950 py-8 px-4">
      <Helmet>
        <title>{t('onboarding.meta.title')}</title>
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href="https://hmonghearts.com/onboarding" />
      </Helmet>
      <div className="max-w-md sm:max-w-lg mx-auto">
        <ProgressBar currentStep={step} labels={steps.map((s) => s.label)} />

        <div
          data-testid="onboarding-form"
          className="bg-white dark:bg-gray-900 rounded-xl shadow-xl p-4 sm:p-6 animate-slide-up"
        >
          {errors._global && (
            <div
              data-testid="onboarding-error"
              className="mb-4 p-3 bg-red-50 dark:bg-red-950/30 text-red-600 dark:text-red-400 rounded-xl text-sm"
            >
              {errors._global}
            </div>
          )}

          {renderStep()}

          <div className="flex flex-col sm:flex-row justify-between mt-8 gap-3">
            {step > 1 ? (
              <Btn
                data-testid="onboarding-btn-back"
                v="ghost"
                onClick={handleBack}
                icon={ArrowLeft}
                className="text-sm py-2.5 order-2 sm:order-1"
              >
                {t('onboarding.btnBack')}
              </Btn>
            ) : (
              <div className="hidden sm:block" />
            )}

            {isLastStep ? (
              <Btn
                data-testid="onboarding-btn-submit"
                onClick={handleSubmit}
                icon={loading ? Loader : Check}
                loading={loading}
                className="w-full sm:w-auto text-sm py-2.5 order-1 sm:order-2"
              >
                {t('onboarding.btnFinish')}
              </Btn>
            ) : (
              <Btn
                data-testid="onboarding-btn-next"
                onClick={handleNext}
                icon={ArrowRight}
                className="w-full sm:w-auto text-sm py-2.5 order-1 sm:order-2"
              >
                {t('onboarding.btnContinue')}
              </Btn>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default OnboardingPage;
